/*----------------------------------
- GENERATED FILE
----------------------------------*/

// This file is generated by Proteum from client/pages/marketing/recruiters.tsx.
// Do not edit it manually.

import type __GeneratedRouteApp from "@/client/index";

/*----------------------------------
- DEPENDANCES
----------------------------------*/

import React from 'react';
import { ArrowRight, Award, Briefcase, DollarSign, Globe, Shield, Target, TrendingUp, Users, Zap } from 'lucide-preact';

/*----------------------------------
- APP
----------------------------------*/

import Motion, { fadeIn } from '@/client/components/Motion';
import ClientLogosCarousel from '@/client/components/marketing/ClientLogosCarousel';
import PublicFooter from '@/client/components/marketing/PublicFooter';
import PublicHeader from '@/client/components/marketing/PublicHeader';
import Page from '@/client/pages/_layout/_page';
import { MARKETING_URLS } from '@/client/catalogs/marketing/navigation';

/*----------------------------------
- CONTROLEUR
----------------------------------*/

export const __register = (app: __GeneratedRouteApp) => {
  const { Router } = app;
  return Router.page('/recruiters', { ...({
  static: {
    refresh: '1h',
    urls: ['/recruiters']
  }
}), ...{ filepath: "/app/client/pages/marketing/recruiters.tsx", sourceLocation: { line: 24, column: 1 }, id: "marketing_recruiters" } }, null, ({}) => <Page withLayout={false}>
        <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-violet-50">
            <PublicHeader currentPage="recruiters" ctaLabel="Join as Recruiter" ctaHref={MARKETING_URLS.platformSignup} />

            <section className="pt-32 pb-20 px-6 lg:px-8">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <Motion tag="div" initial="hidden" animate="visible" variants={fadeIn}>
                            <h1 className="text-5xl lg:text-6xl font-bold bg-gradient-to-r from-orange-600 via-pink-600 to-violet-600 bg-clip-text text-transparent mb-6">
                                Join the World&apos;s Leading Recruitment Marketplace
                            </h1>
                            <p className="text-xl text-slate-700 mb-8 leading-relaxed">
                                Access exclusive recruitment mandates from companies worldwide. Get matched with positions that fit your expertise, location, and success rate.
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4">
                                <a href={MARKETING_URLS.platformSignup} className="bg-gradient-to-r from-orange-600 to-violet-600 hover:from-orange-700 hover:to-violet-700 text-white px-8 py-4 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl flex items-center justify-center gap-2">
                                    Join as Recruiter
                                    <ArrowRight size={20} />
                                </a>
                                <a href={MARKETING_URLS.platform} className="border-2 border-orange-600 text-orange-600 hover:bg-orange-50 px-8 py-4 rounded-xl font-semibold transition-all text-center">
                                    Browse Missions
                                </a>
                            </div>
                        </Motion>

                        <Motion tag="div" initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.2
          }} className="bg-white rounded-2xl shadow-2xl p-8 border border-orange-100">
                            <div className="grid grid-cols-2 gap-6">
                                {[{
                value: '10,000+',
                label: 'Active Recruiters',
                className: 'from-orange-600 to-violet-600'
              }, {
                value: '800+',
                label: 'Companies',
                className: 'from-violet-600 to-purple-600'
              }, {
                value: '70%',
                label: 'Interview Rate',
                className: 'from-pink-600 to-emerald-600'
              }, {
                value: '3 days',
                label: 'Avg. First Candidate',
                className: 'from-orange-600 to-pink-600'
              }].map(stat => <div key={stat.label} className="text-center">
                                        <div className={`text-4xl font-bold bg-gradient-to-r ${stat.className} bg-clip-text text-transparent mb-2`}>
                                            {stat.value}
                                        </div>
                                        <p className="text-slate-600 font-medium">{stat.label}</p>
                                    </div>)}
                            </div>
                        </Motion>
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-emerald-50">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="h2" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-4xl font-bold text-center bg-gradient-to-r from-orange-600 to-violet-600 bg-clip-text text-transparent mb-12">
                        How It Works for Recruiters
                    </Motion>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[{
            step: '01',
            icon: Target,
            title: 'Get Matched',
            description: 'Our AI matches you with missions based on your sector expertise, geography, and performance KPIs.'
          }, {
            step: '02',
            icon: Users,
            title: 'Apply and Collaborate',
            description: 'Apply to relevant positions and work alongside other specialists on the same mandate.'
          }, {
            step: '03',
            icon: DollarSign,
            title: 'Get Paid Fast',
            description: 'Track your candidates, placements, and commissions in real time with transparent terms.'
          }].map((item, index) => <Motion tag="div" key={item.step} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-2xl p-8 border-2 border-orange-100 hover:border-orange-300 hover:shadow-xl transition-all">
                                <div className="text-orange-600 font-bold text-sm mb-4">{item.step}</div>
                                <div className="w-14 h-14 bg-gradient-to-br from-orange-100 to-violet-100 rounded-xl flex items-center justify-center mb-6">
                                    <item.icon className="w-7 h-7 text-orange-600" />
                                </div>
                                <h3 className="text-xl font-bold text-slate-900 mb-3">{item.title}</h3>
                                <p className="text-slate-700 leading-relaxed">{item.description}</p>
                            </Motion>)}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-orange-50 to-violet-50">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="h2" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-4xl font-bold text-center bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent mb-12">
                        Why Top Recruiters Join CrossPath
                    </Motion>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {[{
            icon: Briefcase,
            title: 'Exclusive Mandates',
            description: 'Access high-quality recruitment missions from verified companies.'
          }, {
            icon: Globe,
            title: 'Global Reach',
            description: 'Work on positions across geographies and industries.'
          }, {
            icon: Zap,
            title: 'Smart Matching',
            description: 'AI matches you with positions that fit your expertise and success rate.'
          }, {
            icon: Shield,
            title: 'Transparent Terms',
            description: 'Clear mission terms with full transparency and tracking.'
          }, {
            icon: TrendingUp,
            title: 'Performance Tracking',
            description: 'Build your reputation with verified KPIs and success metrics.'
          }, {
            icon: Award,
            title: 'Premium Support',
            description: 'Dedicated account management and full platform support.'
          }].map((benefit, index) => <Motion tag="div" key={benefit.title} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.05
          }} className="bg-white rounded-xl p-6 border-2 border-teal-100 hover:border-teal-300 hover:shadow-lg transition-all">
                                <div className="w-12 h-12 bg-gradient-to-br from-orange-100 to-violet-100 rounded-lg flex items-center justify-center mb-4">
                                    <benefit.icon className="w-6 h-6 text-orange-600" />
                                </div>
                                <h3 className="text-lg font-bold text-slate-900 mb-2">{benefit.title}</h3>
                                <p className="text-slate-700 text-sm leading-relaxed">{benefit.description}</p>
                            </Motion>)}
                    </div>
                </div>
            </section>

            <section className="py-20 px-6 lg:px-8 bg-gradient-to-br from-white to-violet-50">
                <div className="max-w-6xl mx-auto">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn} className="text-center mb-12">
                        <h2 className="text-4xl font-bold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent mb-4">
                            Active Missions Right Now
                        </h2>
                        <p className="text-slate-600 text-lg">Browse and apply to positions matching your expertise</p>
                    </Motion>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {[{
            title: 'Senior Software Engineer',
            company: 'Tech Startup',
            location: 'London, UK',
            salary: '£70,000 - £90,000',
            applicants: 3
          }, {
            title: 'Marketing Director',
            company: 'E-commerce Scale-up',
            location: 'Paris, France',
            salary: '€80,000 - €100,000',
            applicants: 2
          }, {
            title: 'Head of Sales',
            company: 'SaaS Company',
            location: 'Dubai, UAE',
            salary: '$120,000 - $150,000',
            applicants: 4
          }, {
            title: 'Product Manager',
            company: 'Fintech Unicorn',
            location: 'Remote',
            salary: '€65,000 - €85,000',
            applicants: 5
          }].map((mission, index) => <Motion tag="div" key={`${mission.title}-${mission.company}`} initial="hidden" whileInView="visible" viewport={{
            once: true
          }} variants={fadeIn} transition={{
            delay: index * 0.1
          }} className="bg-white rounded-xl p-6 border-2 border-cyan-100 hover:border-cyan-300 hover:shadow-xl transition-all">
                                <div className="flex items-start justify-between mb-4 gap-4">
                                    <div>
                                        <h3 className="text-xl font-bold text-slate-900 mb-1">{mission.title}</h3>
                                        <p className="text-slate-600 text-sm">{mission.company}</p>
                                    </div>
                                    <span className="bg-gradient-to-r from-orange-500 to-violet-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                                        Open mission
                                    </span>
                                </div>
                                <div className="space-y-2 mb-4">
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Globe size={16} className="text-slate-400" />
                                        <span>{mission.location}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <DollarSign size={16} className="text-slate-400" />
                                        <span>{mission.salary}</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-sm text-slate-700">
                                        <Users size={16} className="text-slate-400" />
                                        <span>{mission.applicants} recruiters applied</span>
                                    </div>
                                </div>
                                <a href={MARKETING_URLS.platformSignup} className="w-full bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-blue-700 text-white py-2 rounded-lg font-semibold transition-all text-sm block text-center">
                                    View Details
                                </a>
                            </Motion>)}
                    </div>

                    <div className="text-center mt-8">
                        <a href={MARKETING_URLS.platform} className="bg-gradient-to-r from-violet-600 to-purple-600 hover:from-violet-700 hover:to-blue-700 text-white px-8 py-3 rounded-xl font-semibold transition-all shadow-lg hover:shadow-xl inline-flex items-center gap-2">
                            Browse All Missions
                            <ArrowRight size={20} />
                        </a>
                    </div>
                </div>
            </section>

            <ClientLogosCarousel />

            <section className="py-24 px-6 lg:px-8 bg-gradient-to-r from-orange-600 via-pink-600 to-violet-600">
                <div className="max-w-4xl mx-auto text-center">
                    <Motion tag="div" initial="hidden" whileInView="visible" viewport={{
          once: true
        }} variants={fadeIn}>
                        <h2 className="text-4xl lg:text-5xl font-bold text-white mb-4">
                            Ready to Scale Your Recruitment Business?
                        </h2>
                        <p className="text-xl text-white/90 mb-8">Join 10,000+ recruiters already using CrossPath</p>
                        <a href={MARKETING_URLS.platformSignup} className="bg-white text-orange-600 hover:bg-orange-50 px-10 py-4 rounded-xl font-semibold text-lg transition-all shadow-2xl hover:shadow-2xl hover:scale-105 transform inline-flex items-center gap-2">
                            Create Recruiter Account
                            <ArrowRight size={20} />
                        </a>
                    </Motion>
                </div>
            </section>

            <PublicFooter tagline="Join the recruitment marketplace built for independent recruiters and specialist talent partners." />
        </div>
    </Page>);
};
